import React, { useEffect, useRef, useState } from "react";

/**
 * Minimal PayPal test button (frontend-only, no backend).
 *
 * - Loads PayPal JS SDK on mount (once, globally deduped via script id).
 * - Renders paypal.Buttons() into a container.
 * - Test amount: 1.00 EUR (override via `amount` prop).
 * - On approval: console.log("PLAN PURCHASED:", planId) + alert.
 * - Nothing else: no DB, no user state mutation.
 *
 * Client ID comes from REACT_APP_PAYPAL_CLIENT_ID. If it's the default
 * placeholder we render a disabled hint instead of loading the SDK.
 */
const CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID || "";
const SDK_ID = "paypal-js-sdk";

function loadPayPalSdk(clientId) {
  return new Promise((resolve, reject) => {
    if (typeof window === "undefined") return reject(new Error("no window"));
    if (window.paypal) return resolve(window.paypal);

    const existing = document.getElementById(SDK_ID);
    if (existing) {
      existing.addEventListener("load", () => resolve(window.paypal));
      existing.addEventListener("error", () => reject(new Error("SDK load failed")));
      return;
    }
    const s = document.createElement("script");
    s.id = SDK_ID;
    s.src = `https://www.paypal.com/sdk/js?client-id=${encodeURIComponent(clientId)}&currency=EUR`;
    s.async = true;
    s.onload = () => resolve(window.paypal);
    s.onerror = () => reject(new Error("SDK load failed"));
    document.body.appendChild(s);
  });
}

export default function TestPayPalButton({
  planId = "test",
  amount = "1.00",
  label = "Pagamento test 1,00 €",
  dark = true,
}) {
  const containerRef = useRef(null);
  const renderedRef = useRef(false);
  const [error, setError] = useState(null);

  const isPlaceholder = !CLIENT_ID || CLIENT_ID === "YOUR_CLIENT_ID";

  useEffect(() => {
    if (isPlaceholder) return;
    if (renderedRef.current) return;

    let cancelled = false;
    loadPayPalSdk(CLIENT_ID)
      .then((paypal) => {
        if (cancelled || !containerRef.current || renderedRef.current) return;
        renderedRef.current = true;
        paypal
          .Buttons({
            style: { layout: "vertical", height: 40, tagline: false, shape: "pill" },
            createOrder: function (data, actions) {
              return actions.order.create({
                purchase_units: [{
                  amount: { value: String(amount) },
                  description: `Piano ${planId} (test)`,
                }],
              });
            },
            onApprove: function (data, actions) {
              return actions.order.capture().then(function (details) {
                // eslint-disable-next-line no-console
                console.log("PLAN PURCHASED:", planId);
                // eslint-disable-next-line no-console
                console.log("PAYMENT SUCCESS", details);
                // eslint-disable-next-line no-alert
                alert("Piano attivato: " + planId);
              });
            },
            onError: function (err) {
              // eslint-disable-next-line no-console
              console.error("PayPal error", err);
              setError("Errore durante il pagamento");
            },
          })
          .render(containerRef.current);
      })
      .catch((e) => {
        // eslint-disable-next-line no-console
        console.error(e);
        setError("Impossibile caricare PayPal");
      });

    return () => { cancelled = true; };
  }, [isPlaceholder, planId, amount]);

  const labelCls = dark ? "text-white/60" : "text-[#737373]";
  const hintBg = dark ? "border-white/20 bg-white/5 text-white/70" : "border-[#E5E5E0] bg-[#FAFAFA] text-[#737373]";
  const codeCls = dark ? "text-white/90" : "text-[#1A1A1A]";

  return (
    <div data-testid={`paypal-wrapper-${planId}`}>
      <p className={`text-[11px] uppercase tracking-[0.2em] mb-2 text-center ${labelCls}`}>
        {label}
      </p>
      {isPlaceholder ? (
        <div
          className={`rounded-xl border text-xs px-4 py-3 text-center ${hintBg}`}
          data-testid={`paypal-placeholder-${planId}`}
        >
          Imposta <code className={codeCls}>REACT_APP_PAYPAL_CLIENT_ID</code> in <code className={codeCls}>frontend/.env</code> per abilitare il pagamento.
        </div>
      ) : (
        <>
          <div
            ref={containerRef}
            data-testid={`paypal-button-${planId}`}
          />
          {error && (
            <p className="text-xs text-rose-400 text-center mt-2" data-testid={`paypal-error-${planId}`}>{error}</p>
          )}
        </>
      )}
    </div>
  );
}

